import { useEffect, useCallback, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useAccent } from "@/context/AccentContext";
import { MorningMenu } from "@/components/home/MorningMenu";
import { MiluCall } from "@/components/MiluCall";
import { MiluDrill } from "@/components/MiluDrill";
import { MiluQuiz } from "@/components/MiluQuiz";
import { useWakeWord } from "@/components/MiluWake";
import { READ_VOICES } from "@/components/VoiceControls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Brain, CalendarPlus, Check, ChevronDown, ChevronUp, Ear, EarOff, GraduationCap, History, Loader2, Mic, Moon, Phone, PhoneOff, RefreshCw, RotateCcw, Send, Sparkles, Square, Volume2, VolumeX, X } from "lucide-react";

const HIDE_KEY = "chatmilu.milu.hidden";
const VOICE_KEY = "chatmilu.milu.voice";
const SESSION_KEY = "chatmilu.milu.session";
const CHAT_KEY = "chatmilu.milu.chat";
const CELEBRATE_KEY = "chatmilu.milu.celebrate";
const GOAL_WEEK_KEY = "chatmilu.milu.goalweek";
const TONIGHT_KEY = "chatmilu.milu.tonight";
const EVENING_KEY = "chatmilu.milu.evening";
const NIGHT_KEY = "chatmilu.milu.goodnight";
const RECAP_KEY = "chatmilu.milu.memoryweek";
const NIGHTS_KEY = "chatmilu.milu.nightsweek";
const HOUR_EYES_KEY = "chatmilu.milu.houreyes";
const HIDE_DAYS = 3;

const SPRITES = {
  idle: "/wingman.png",
  wave: "/milu-wave.png",
  cheer: "/milu-cheer.png",
  think: "/milu-think.png",
};

const EYE_MOODS = [["listen", "listening"], ["think", "thinking"], ["cheer", "celebrating"]];

const EYE_HEX = {
  electric: "#1A7DFF", jade: "#2FBF9B", amber: "#E0782F", violet: "#9B59D0", crimson: "#D6415C",
  slate: "#8A939E", green: "#2FB457", teal: "#1F9BB5", magenta: "#D6398C", indigo: "#5A55CF",
};

// Where each sprite's eyes sit, measured off the artwork, so the glow lands on them.
const EYES = {
  "/wingman.png": [{ left: "38.51%", top: "33.05%", width: "9.06%", height: "8.35%" },
                   { left: "52.44%", top: "33.05%", width: "9.06%", height: "8.35%" }],
  "/milu-wave.png": [{ left: "42.29%", top: "23.18%", width: "13.03%", height: "7.06%" },
                     { left: "63.08%", top: "23.18%", width: "12.66%", height: "7.06%" }],
  "/milu-think.png": [{ left: "25.48%", top: "17.53%", width: "13.26%", height: "10.30%" },
                      { left: "49.62%", top: "15.21%", width: "13.75%", height: "10.30%" }],
  "/milu-cheer.png": [{ left: "37.23%", top: "29.29%", width: "10.44%", height: "14.80%" },
                      { left: "52.51%", top: "29.29%", width: "10.44%", height: "14.80%" }],
  "/milu-face.png": [{ left: "33.82%", top: "53.91%", width: "21.43%", height: "16.88%" },
                     { left: "67.62%", top: "53.91%", width: "21.00%", height: "16.88%" }],
};

const MiluEyes = ({ sprite, tone = "", hue = "" }) => (EYES[sprite] || []).map((box, i) => (
  <span key={`eye-${i}`} aria-hidden className="milu-eye-slot"
        data-testid={`milu-eye-${i === 0 ? "left" : "right"}`} data-tone={tone || "calm"}
        data-hue={hue || "default"}
        style={box}>
    <span className={`milu-eye ${tone ? `milu-eye--${tone}` : ""}`}
          style={hue ? { background: `radial-gradient(closest-side, #ffffff, ${hue} 58%, transparent 100%)`,
                         filter: `drop-shadow(0 0 8px ${hue})` } : undefined} />
  </span>
));

// His eyes drift towards whoever is moving nearby, then settle back
const useEyesFollow = (ref) => {
  useEffect(() => {
    const near = 320;  // px: how close a cursor has to be before he notices
    let waiting = false;
    const onMove = (e) => {
      if (waiting || !ref.current) return;
      waiting = true;
      requestAnimationFrame(() => {
        waiting = false;
        const el = ref.current;
        if (!el) return;
        const box = el.getBoundingClientRect();
        if (!box.width) return;
        const dx = e.clientX - (box.left + box.width / 2);
        const dy = e.clientY - (box.top + box.height * 0.32);
        const away = Math.hypot(dx, dy);
        const pull = away > near ? 0 : 1 - away / near;
        const reach = Math.max(4, box.width * 0.07);  // he looks further when he is drawn larger
        el.style.setProperty("--eye-x", `${Math.max(-1, Math.min(1, dx / 180)) * reach * pull}px`);
        el.style.setProperty("--eye-y", `${Math.max(-1, Math.min(1, dy / 180)) * reach * 0.6 * pull}px`);
        // and his arms come round with them: he leans and reaches towards you
        const side = Math.max(-1, Math.min(1, dx / 220));
        const lift = Math.max(-1, Math.min(1, dy / 220));
        el.style.setProperty("--milu-tilt", `${side * 11 * pull}deg`);
        el.style.setProperty("--milu-reach", `${side * 9 * pull}px`);
        el.style.setProperty("--milu-rise", `${Math.max(0, -lift) * 6 * pull}px`);
      });
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [ref]);
};

const MOOD_ANIM = {
  idle: "milu-idle",
  wave: "milu-waving",
  cheer: "milu-cheering",
  think: "milu-thinking",
};

// He greets everywhere except the places he'd be in the way.
const HIDDEN_PATHS = ["/admin", "/login", "/signup", "/join", "/invite"];
// Mascot only, no pitch card — pages people are already working in.
const QUIET_PATHS = ["/chat", "/messages", "/bedtime", "/stories", "/creations", "/tools", "/phrasebook",
  "/settings", "/payment"];
// The only places the "back to our call" bar stays out of the way.
const NO_BAR_PATHS = ["/login", "/signup"];
// Pages with a fixed bottom composer he must clear on phones.
const LIFT_PATHS = ["/chat", "/messages", "/bedtime"];

const PITCH_FALLBACK = {
  say: "Psst — want to talk to anyone in 50 languages?",
  chips: ["What can you do?", "Show me the plans"],
};

const msgId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

const hiddenNow = () => Number(localStorage.getItem(HIDE_KEY) || 0) > Date.now();

export const MiluAssistant = ({ quiet, liftForComposer }) => {
  const { lang, languages } = useI18n();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const hidden = HIDDEN_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const isQuiet = quiet ?? QUIET_PATHS.some((p) => pathname.startsWith(p));
  const noBar = NO_BAR_PATHS.some((p) => pathname.startsWith(p));
  const lift = liftForComposer ?? LIFT_PATHS.some((p) => pathname.startsWith(p));
  const [state, setState] = useState({ show: false, pitches: [], intro: "" });
  const [pitchIdx, setPitchIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(hiddenNow());
  const [messages, setMessages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CHAT_KEY) || "[]").slice(-20);
    } catch {
      return [];
    }
  });
  const [chips, setChips] = useState([]);
  const [cta, setCta] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [voiceOn, setVoiceOn] = useState(localStorage.getItem(VOICE_KEY) === "1");
  const [speaking, setSpeaking] = useState(false);
  const [calling, setCalling] = useState(false);
  const [wake, setWake] = useState(() => localStorage.getItem("cml_wake") === "1");
  const [todos, setTodos] = useState([]);
  const [pastCalls, setPastCalls] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [trend, setTrend] = useState([]);
  const [levels, setLevels] = useState(null);
  const [tongues, setTongues] = useState(false);
  const [drill, setDrill] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [tonight, setTonight] = useState(null);
  const [minds, setMinds] = useState(null);
  const [night, setNight] = useState(null);
  const [weekNights, setWeekNights] = useState(null);
  const [learned, setLearned] = useState(null);
  const [jotted, setJotted] = useState("");
  const [eyeWish, setEyeWish] = useState(user?.eye_colour || "");
  const [eyeMoods, setEyeMoods] = useState(user?.eye_moods || {});
  const [byHour, setByHour] = useState(localStorage.getItem(HOUR_EYES_KEY) !== "0");
  const { accent } = useAccent();

  useEffect(() => setEyeWish(user?.eye_colour || ""), [user?.eye_colour]);
  useEffect(() => setEyeMoods(user?.eye_moods || {}), [user?.eye_moods]);

  const wearEyes = async (name, mood = "") => {
    const now_ = mood ? eyeMoods[mood] : eyeWish;
    const next = now_ === name ? "" : name;
    if (mood) setEyeMoods((was) => ({ ...was, [mood]: next }));
    else setEyeWish(next);
    await api.put("/wingman/eyes", { colour: next, mood }).catch(() => {});
  };

  // a colour asked for out loud on a call, or typed in chat
  const applyEyes = (told) => {
    const [one, two] = String(told || "").split(":");
    if (two) setEyeMoods((was) => ({ ...was, [one]: two }));
    else setEyeWish(one);
  };

  // his own colour of the hour: bright with your theme by day, warm at dusk, deep at night
  const hourHue = () => {
    const hour = new Date().getHours();
    if (hour >= 22 || hour < 5) return EYE_HEX.indigo;
    if (hour >= 18) return EYE_HEX.amber;
    return accent || "";
  };

  // mood first, then the colour you chose, then the hour and your theme
  const eyeHue = (tone = "") =>
    EYE_HEX[eyeMoods[tone]] || EYE_HEX[eyeWish] || (byHour ? hourHue() : accent || "");

  const keepNight = async () => {
    if (!jotted.trim()) return;
    try {
      const { data } = await api.post("/wingman/journal", { note: jotted.trim() });
      toast.success(data.treat || "Kept — you can look back on it any time");
      if (data.treat) setReward(data.treat);
      setJotted("");
      setNight(null);    } catch {
      toast.error("Could not keep that one");
    }
  };
  const lessonRef = useRef(null);
  // a call cut short by a page reload can be picked back up with one tap — it waits until he is asked
  const [resume, setResume] = useState(() => Boolean(localStorage.getItem("cml_call_at")));

  useEffect(() => {
    if (calling) {
      localStorage.setItem("cml_call_at", String(Date.now()));
      setResume(false);
    }
  }, [calling]);
  const [listening, setListening] = useState(false);
  const [greeting, setGreeting] = useState(true);
  const [cheering, setCheering] = useState(false);
  const [celebrate, setCelebrate] = useState(() => localStorage.getItem(CELEBRATE_KEY));
  const [reward, setReward] = useState(null);
  const sessionRef = useRef(`ml_${Math.random().toString(36).slice(2, 10)}`);
  const feedRef = useRef(null);
  const audioRef = useRef(null);
  const recorderRef = useRef(null);

  const track = (type, detail = "") =>
    api.post("/wingman/event", { session_id: sessionRef.current, type, detail }).catch(() => {});

  useEffect(() => {
    if (gone) return;
    api
      .get("/wingman/opener", { params: { lang } })
      .then(({ data }) => setState(data))
      .catch(() => {});
  }, [gone, lang]);

  useEffect(() => {
    if (open || !state.pitches?.length) return;
    const id = setInterval(() => setPitchIdx((i) => (i + 1) % state.pitches.length), 7000);
    return () => clearInterval(id);
  }, [open, state.pitches]);

  useEffect(() => {
    if (lesson) lessonRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [lesson]);

  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => () => audioRef.current?.pause(), []);

  useEffect(() => {
    const onPractise = (e) => {
      const { kind, lang: code } = e.detail || {};
      setOpen(true);
      setTongues(false);
      if (kind === "quiz") {
        setDrill(null);
        setQuiz(code || "all");
      } else {
        setQuiz(null);
        setDrill(code || "");
      }
    };
    window.addEventListener("chatmilu:practise", onPractise);
    return () => window.removeEventListener("chatmilu:practise", onPractise);
  }, []);

  useEffect(() => {
    const onReward = (e) => setReward(e.detail);
    window.addEventListener("chatmilu:reward", onReward);
    return () => window.removeEventListener("chatmilu:reward", onReward);
  }, []);

  useEffect(() => {
    if (!reward) return;
    const id = setTimeout(() => setReward(null), 16000);
    return () => clearTimeout(id);
  }, [reward]);

  useEffect(() => {
    if (!celebrate) return;
    localStorage.removeItem(CELEBRATE_KEY);
    const id = setTimeout(() => setCelebrate(null), 14000);
    return () => clearTimeout(id);
  }, [celebrate]);

  useEffect(() => {
    const id = setTimeout(() => setGreeting(false), 5200);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!cheering) return;
    const id = setTimeout(() => setCheering(false), 3200);
    return () => clearTimeout(id);
  }, [cheering]);

  const say = async (text) => {
    if (!text) return;
    try {
      audioRef.current?.pause();
      setSpeaking(true);
      const res = await api.post("/wingman/speak", { text }, { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(res.data));
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      await audio.play();
    } catch {
      setSpeaking(false);
    }
  };

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem(VOICE_KEY, next ? "1" : "0");
    if (!next) {
      audioRef.current?.pause();
      setSpeaking(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(HIDE_KEY, String(Date.now() + HIDE_DAYS * 864e5));
    audioRef.current?.pause();
    track("dismissed");
    setOpen(false);
    setGone(true);
  };

  const startFresh = () => {
    audioRef.current?.pause();
    localStorage.removeItem(CHAT_KEY);
    sessionRef.current = `ml_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, sessionRef.current);
    setMessages([]);
    setChips([]);
    track("fresh");
    const opener = state.intro || "Hi, my name is MiLu. What would you like to know?";
    setMessages([{ id: msgId(), role: "milu", text: opener }]);
    setChips(state.pitches?.[0]?.chips || []);
    if (voiceOn) say(opener);
  };

  const bringBack = () => {
    localStorage.removeItem(HIDE_KEY);
    setGone(false);
    track("reopened");
    startRef.current?.();
  };

  const [spot, setSpot] = useState(null);
  const [morning, setMorning] = useState(null);

  // the morning menu: MiLu offers to read the day in, from the digest hour onwards
  useEffect(() => {
    if (!user || hidden) return;
    api.get("/news/morning", { params: { lang } })
      .then(({ data }) => data.show && data.choices?.length && setMorning(data))
      .catch(() => {});
  }, [user, hidden, lang]);

  const morningDone = () => api.post("/news/morning/seen").catch(() => {});

  // the same choices, offered all day at the top of his panel
  const [listenMenu, setListenMenu] = useState(null);
  const [listenOpen, setListenOpen] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // a choice tapped in the morning email lands here: ?listen=news
  const [autoPick] = useState(() => new URLSearchParams(window.location.search).get("listen") || "");

  // the language the stories are read in — his own choice, kept apart from the app's language
  const [readLang, setReadLang] = useState(() => localStorage.getItem("cml_listen_lang") || lang);

  const loadListen = async (refresh = false, said = readLang) => {
    if (refresh) setRefreshing(true);
    try {
      const { data } = await api.get("/news/morning", { params: { lang: said, force: true, refresh } });
      setListenMenu(data.choices?.length ? data : null);
    } catch {
      /* his menu is a nicety, never a blocker */
    } finally {
      setRefreshing(false);
    }
  };

  const readIn = (code) => {
    setReadLang(code);
    localStorage.setItem("cml_listen_lang", code);
    setListenMenu(null);
    loadListen(false, code);
  };

  // each language keeps its own voice
  const readWith = async (voice) => {
    setListenMenu((was) => (was ? { ...was, voice } : was));
    await api.put("/news/morning/voice", { lang: readLang, voice }).catch(() => {});
  };

  // a lesson report, with how the last few lessons went beside it
  // how each practice language is going, plus this week's goal — and a cheer the first time it is hit
  const loadLevels = useCallback(async () => {
    try {
      const { data } = await api.get("/wingman/lessons");
      setLevels(data);
      const week = data.week || {};
      if (week.hit && localStorage.getItem(GOAL_WEEK_KEY) !== week.since) {
        localStorage.setItem(GOAL_WEEK_KEY, week.since);
        setReward(`${week.done} practice calls this week — that was your goal. Look at you!`);
      }
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (user) loadLevels();
  }, [user, loadLevels]);

  // one line to say out loud, shown in the app in the evening — email or no email
  useEffect(() => {
    if (!user) return;
    api.get("/wingman/tonight").then(({ data }) => {
      if (!data.line || data.hour < 19) return;
      if (localStorage.getItem(TONIGHT_KEY) === data.day) return;
      setTonight(data);
    }).catch(() => {});
  }, [user]);

  const closeTonight = () => {
    if (tonight?.day) localStorage.setItem(TONIGHT_KEY, tonight.day);
    setTonight(null);
  };

  // MiLu opens the evening himself, once a day, by name
  useEffect(() => {
    if (!user) return;
    api.get("/wingman/evening").then(({ data }) => {
      if (!data.say || localStorage.getItem(EVENING_KEY) === data.day) return;
      localStorage.setItem(EVENING_KEY, data.day);
      setOpen(true);
      setMessages((was) => [...was, { id: msgId(), role: "milu", text: data.say }]);
      if (voiceOn) say(data.say);
    }).catch(() => {});
  }, [user]);   // eslint-disable-line react-hooks/exhaustive-deps

  // the calm close of the day: what tomorrow holds, then goodnight
  useEffect(() => {
    if (!user) return;
    api.get("/wingman/goodnight").then(({ data }) => {
      if (!data.say || localStorage.getItem(NIGHT_KEY) === data.day) return;
      localStorage.setItem(NIGHT_KEY, data.day);
      setNight(data);
    }).catch(() => {});
  }, [user]);

  // once a week, what he has picked up about them
  useEffect(() => {
    if (!user) return;
    api.get("/wingman/memory/recap").then(({ data }) => {
      if (!data.learned?.length || localStorage.getItem(RECAP_KEY) === data.week) return;
      localStorage.setItem(RECAP_KEY, data.week);
      setLearned(data);
    }).catch(() => {});
  }, [user]);

  // Sunday evening: the week of nights, read back
  useEffect(() => {
    if (!user) return;
    api.get("/wingman/journal/recap").then(({ data }) => {
      if (!data.due || localStorage.getItem(NIGHTS_KEY) === data.week) return;
      localStorage.setItem(NIGHTS_KEY, data.week);
      setWeekNights(data);
    }).catch(() => {});
  }, [user]);

  const loadMinds = () => {
    setListenOpen(false);
    return api.get("/wingman/memory").then(({ data }) => setMinds(data.memories || []))
      .catch(() => setMinds([]));
  };

  const forget = async (id) => {
    setMinds((was) => (was || []).filter((m) => m.memory_id !== id));
    await api.delete(`/wingman/memory/${id}`).catch(() => {});
  };

  const setGoal = async (goal) => {
    setLevels((was) => (was ? { ...was, week: { ...was.week, goal } } : was));
    await api.put("/wingman/goal", { goal }).catch(() => {});
    loadLevels();
  };

  const langInfo = (code) => levels?.languages?.find((l) => l.lang === code);

  const showLesson = (marks) => {
    setLesson(marks);
    setOpen(true);
    setPastCalls(null);
    api.get("/wingman/calls")
      .then(({ data }) => setTrend((data.calls || [])
        .filter((c) => c.lesson?.score)
        .slice(0, 6)
        .map((c) => ({ score: c.lesson.score, at: c.ended_at }))
        .reverse()))
      .catch(() => setTrend([]));
    loadLevels();
  };

  const keepRecap = (recap, jobs, marks) => {
    const listed = jobs.length ? `\n\n${jobs.map((t) => `• ${t}`).join("\n")}` : "";
    if (marks) showLesson(marks);
    if (marks?.level_up) {
      const named = languages?.find((l) => l.code === marks.lang)?.name
        || String(marks.lang || "").toUpperCase();
      setReward(`Your ${named} just moved up to ${marks.level_up}. I knew you had it in you.`);
    }
    setMessages((was) => [...was, { id: msgId(), role: "milu", text: `${recap}${listed}` }]);
    setTodos(jobs);
    toast.success("Call written up in our chat");
  };

  // the to-dos from a call, straight onto today's calendar
  const toCalendar = async () => {
    const day = new Date().toISOString().slice(0, 10);
    try {
      await Promise.all(todos.map((title) => api.post("/calendar/events", {
        title: title.slice(0, 120), day, all_day: true, colour: "blue",
        notes: "From a call with MiLu",
      })));
      toast.success(`${todos.length} on your calendar for today`);
      setTodos([]);
    } catch {
      toast.error("Could not add those to your calendar");
    }
  };

  const showCalls = async () => {
    if (pastCalls) return setPastCalls(null);
    setListenOpen(false);
    setTongues(false);
    try {
      const { data } = await api.get("/wingman/calls");
      setPastCalls(data.calls || []);
    } catch {
      setPastCalls([]);
    }
  };

  useWakeWord(wake && !calling && Boolean(user), () => {
    setCalling(true);
    toast.success("MiLu heard you");
  });

  const bubbleRef = useRef(null);
  const mascotRef = useRef(null);
  const morningRef = useRef(null);
  useEyesFollow(bubbleRef);
  useEyesFollow(mascotRef);
  useEyesFollow(morningRef);

  useEffect(() => {
    if (open && user && !listenMenu) loadListen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  useEffect(() => {
    if (!autoPick || !user) return;
    setOpen(true);
    window.history.replaceState({}, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPick, user]);

  useEffect(() => {
    if (messages.length === 0) return;
    localStorage.setItem(CHAT_KEY, JSON.stringify(messages.slice(-20)));
  }, [messages]);
  const startRef = useRef(null);

  useEffect(() => {
    const place = () => {
      const anchor = document.getElementById("milu-anchor");
      if (!anchor || window.innerWidth < 1024) {
        setSpot(null);
        return;
      }
      const box = anchor.getBoundingClientRect();
      if (box.bottom < 80 || box.top > window.innerHeight - 120) {
        setSpot(null);   // the hero has scrolled away, back to the corner
        return;
      }
      const room = window.innerWidth - 440;
      setSpot({ top: Math.max(box.top - 150, 12),
                left: Math.min(box.left + 340, Math.max(room, box.left)),
                right: "auto", bottom: "auto" });
    };
    place();
    window.addEventListener("scroll", place, { passive: true });
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place);
      window.removeEventListener("resize", place);
    };
  }, [pathname]);

  const listen = async () => {
    if (listening) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false);
        const blob = new Blob(chunks, { type: "audio/webm" });
        if (blob.size < 1200) return;
        setBusy(true);
        try {
          const form = new FormData();
          form.append("audio", blob, "voice.webm");
          const { data } = await api.post("/wingman/listen", form);
          setBusy(false);
          if (data.text) {
            track("voice_in");
            send(data.text);
          } else {
            toast.info("I didn't catch that — try again a bit closer to the mic");
          }
        } catch (e) {
          setBusy(false);
          toast.error(apiError(e.response?.data?.detail) || "I couldn't hear that");
        }
      };
      recorder.start();
      setListening(true);
    } catch {
      setListening(false);
      toast.error("I need microphone permission to listen");
    }
  };

  const send = async (text) => {
    const body = (text ?? input).trim();
    if (!body || busy) return;
    setInput("");
    setChips([]);
    setCta(null);
    setMessages((m) => [...m, { id: msgId(), role: "user", text: body }]);
    setBusy(true);
    try {
      const { data } = await api.post("/wingman/chat", {
        session_id: sessionRef.current,
        message: body,
        lang,
      });
      setMessages((m) => [...m, { id: msgId(), role: "milu", text: data.say }]);
      if (data.eyes) applyEyes(data.eyes);
      setChips(data.chips || []);
      setCta(data.cta || null);
      if (voiceOn) say(data.say);
    } catch {
      setMessages((m) => [
        ...m,
        { id: msgId(), role: "milu", text: "My circuits slipped — ask me that again?" },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const start = () => {
    setOpen(true);
    if (messages.length > 0) {
      track("resumed");
      const back = state.welcome_back || "Good to see you again — shall we carry on?";
      if (messages[messages.length - 1]?.text !== back) {
        setMessages((all) => [...all, { id: msgId(), role: "milu", text: back }]);
        setChips(state.pitches?.[pitchIdx]?.chips || []);
        if (voiceOn) say(back);
      }
      return;
    }
    if (messages.length === 0) {
      track("opened");
      const p = state.pitches?.[pitchIdx] || PITCH_FALLBACK;
      const intro = state.intro || "Hi, my name is MiLu. I am here at your service.";
      if (!state.show) {  // no pitch for someone already signed in — just a hello
        setMessages([{ id: msgId(), role: "milu", text: intro }]);
        if (voiceOn) say(intro);
        return;
      }
      setMessages([
        { id: msgId(), role: "milu", text: intro },
        { id: msgId(), role: "milu", text: p.say },
      ]);
      setChips(p.chips || []);
      if (voiceOn) say(`${intro} ${p.say}`);
    }
  };

  startRef.current = start;

  // the bar sits bottom-left so it never covers him, and shows everywhere but the sign-in pages
  const resumeBar = !calling && resume && user && !noBar ? (
    <div
      data-testid="milu-call-resume"
      className="pointer-events-auto fixed bottom-4 start-4 z-50 flex items-center gap-2 rounded-full border border-brand/40 bg-surface/95 px-3 py-2 shadow-[0_16px_40px_rgba(0,0,0,0.5)] backdrop-blur"
    >
      <img src="/milu-face.png" alt="MiLu" className="h-7 w-7 rounded-full object-cover" />
      <button
        data-testid="milu-call-resume-go"
        onClick={() => setCalling(true)}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-brand"
      >
        <Phone className="h-3 w-3" /> Back to our call
      </button>
      <button
        data-testid="milu-call-resume-close"
        onClick={() => {
          localStorage.removeItem("cml_call_at");
          setResume(false);
        }}
        aria-label="Not now"
        className="text-ink-soft transition-colors hover:text-ink"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  ) : null;

  const renderBody = () => {
  if (calling && (!open || hidden || gone)) {
    return (
      <div
        data-testid="milu-call-float"
        className="pointer-events-auto fixed bottom-4 end-4 z-50 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-3xl border border-brand/30 bg-surface/95 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl"
      >
        <MiluCall
          compact
          sessionId={sessionRef.current}
          lang={lang}
          onEnd={() => {
            localStorage.removeItem("cml_call_at");
            setCalling(false);
          }}
          onRecap={keepRecap}
          onEyes={applyEyes}
          onLine={(who, text) => setMessages((was) => [...was,
            { id: msgId(), role: who === "me" ? "user" : "milu", text }])}
        />
      </div>
    );
  }

  if (hidden) return null;
  if (morning) {
    return (
      <div
        data-testid="milu-morning"
        className={`pointer-events-none fixed bottom-0 end-0 z-40 flex w-full max-w-[420px] flex-col items-end px-4 sm:px-6 ${
          lift ? "pb-24 sm:pb-4" : "pb-2"
        }`}
        style={spot || undefined}
      >
        <div className="pointer-events-auto relative flex max-h-[calc(100vh-5rem)] w-full flex-col">
          <button
            data-testid="milu-morning-close"
            onClick={() => {
              morningDone();
              setMorning(null);
            }}
            aria-label="Not this morning"
            className="absolute end-1 top-1 z-10 rounded-full border border-white/15 bg-surface/90 p-1.5 text-ink-soft backdrop-blur transition-colors hover:text-ink"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="rise min-h-0 overflow-y-auto rounded-3xl border border-brand/25 bg-surface/95 px-5 py-4 pe-10 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-normal uppercase tracking-widest text-brand">
              <Sparkles className="h-3 w-3" /> MiLu
            </p>
            <MorningMenu menu={morning} lang={lang} onUsed={morningDone} />
          </div>
          <span ref={morningRef}
                className="milu-waving pointer-events-auto relative ms-auto -mt-3 block w-fit sm:-mt-4">
            <img
              src={SPRITES.wave}
              alt="MiLu"
              data-testid="milu-morning-mascot"
              className="block h-24 w-auto cursor-pointer select-none drop-shadow-[0_18px_28px_rgba(0,0,0,0.55)] sm:h-40"
              onClick={() => {
                morningDone();
                setMorning(null);
                start();
              }}
            />
            <MiluEyes sprite={SPRITES.wave} hue={eyeHue("wave")} />
          </span>
        </div>
      </div>
    );
  }
  if (gone && !celebrate && !reward) {
    return (
      <div
        data-testid="milu"
        className="pointer-events-none fixed bottom-0 end-0 z-40 flex w-full max-w-[420px] justify-end px-4 pb-4 sm:px-6"
        style={spot || undefined}
      >
        <button
          ref={bubbleRef}
          data-testid="milu-bubble"
          onClick={bringBack}
          aria-label="Talk to MiLu"
          title="Talk to MiLu"
          className="pointer-events-auto relative grid h-16 w-16 place-items-center overflow-hidden rounded-full border border-brand/40 bg-surface/95 shadow-[0_14px_34px_rgba(0,0,0,0.5)] backdrop-blur transition-transform hover:scale-105 active:scale-95"
        >
          <img src="/milu-face.png" alt="MiLu" className="h-full w-full object-cover" />
          <MiluEyes sprite="/milu-face.png" hue={eyeHue()} />
        </button>
      </div>
    );
  }
  if (!celebrate && !reward && !state.show && !user) return null;
  // signed in and past the pitch: he stays reachable, just without the sales line
  const pitchless = !state.show;
  const pitch = reward
    ? { say: reward, chips: [] }
    : celebrate
    ? {
        say: `You're in — ${celebrate} unlocked. Thank you, I'll be right here if you need me!`,
        chips: [],
      }
    : state.pitches?.[pitchIdx] || state.pitches?.[0] || PITCH_FALLBACK;
  const showCard = celebrate || reward || (!isQuiet && !pitchless && scrolled);
  const mood =
    celebrate || reward || cheering ? "cheer" : busy ? "think" : greeting && !open ? "wave" : "idle";
  // his eyes take on the colour of what he is doing
  const eyeTone = listening ? "listen"
    : busy ? "think"
    : celebrate || reward || cheering ? "cheer"
    : speaking ? "speak" : "";

  return (
    <div
      data-testid="milu"
      className={`pointer-events-none fixed bottom-0 end-0 z-40 flex w-full max-w-[420px] flex-col items-end px-4 sm:px-6 ${
        lift ? "pb-24 sm:pb-4" : "pb-2"
      }`}
      style={spot || undefined}
    >
      <div className="pointer-events-auto relative flex max-h-[calc(100vh-5rem)] w-full flex-col">
        <button
          data-testid="milu-dismiss"
          onClick={dismiss}
          aria-label="Hide MiLu"
          className="absolute end-1 top-1 z-10 rounded-full border border-white/15 bg-surface/90 p-1.5 text-ink-soft backdrop-blur transition-colors hover:text-ink"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        {open ? (
          <div
            data-testid="milu-panel"
            className="rise flex min-h-0 flex-col overflow-hidden rounded-3xl border border-brand/25 bg-surface/95 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl"
          >
            <div className="flex shrink-0 items-center gap-1.5 border-b border-white/10 px-5 py-3.5 pe-[10.5rem]">
              <Sparkles className="h-4 w-4 text-brand" />
              <p className="font-display text-sm font-bold tracking-tight">MiLu</p>
              <button
                data-testid="milu-call-start"
                onClick={() => setCalling((v) => !v)}
                title={calling ? "End the call" : "Call MiLu"}
                aria-label={calling ? "End the call" : "Call MiLu"}
                className={`rounded-full border p-1.5 transition-colors ${
                  calling ? "border-destructive/50 text-destructive"
                          : "border-brand/50 text-brand hover:bg-brand/10"}`}
              >
                {calling ? <PhoneOff className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
              </button>
              <button
                data-testid="milu-wake-toggle"
                onClick={() => {
                  const on = !wake;
                  setWake(on);
                  localStorage.setItem("cml_wake", on ? "1" : "0");
                  toast.success(on ? 'Say "Hey MiLu" any time' : "Wake word off");
                }}
                title={wake ? 'Listening for "Hey MiLu"' : 'Listen for "Hey MiLu"'}
                aria-label="Wake word"
                className={`rounded-full border p-1.5 transition-colors ${
                  wake ? "border-pink-400/60 text-pink-300" : "border-white/15 text-ink-soft hover:text-brand"}`}
              >
                {wake ? <Ear className="h-3 w-3" /> : <EarOff className="h-3 w-3" />}
              </button>

              {!user && <span className="truncate text-xs text-ink-soft">· at your service</span>}
              <button
                data-testid="milu-voice-toggle"
                onClick={toggleVoice}
                aria-label={voiceOn ? "Turn MiLu's voice off" : "Turn MiLu's voice on"}
                className={`absolute end-11 top-3 rounded-full border p-1.5 transition-colors ${
                  voiceOn
                    ? "border-brand/50 bg-brand/15 text-brand"
                    : "border-white/15 text-ink-soft hover:text-ink"
                } ${speaking ? "recording" : ""}`}
              >
                {voiceOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
              </button>
              <button
                data-testid="milu-fresh"
                onClick={startFresh}
                aria-label="Start a new chat with MiLu"
                title="Start fresh"
                className="absolute end-[5.25rem] top-3 rounded-full border border-white/15 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
              {user && (
              <select
                data-testid="milu-listen-lang"
                value={readLang}
                onChange={(e) => readIn(e.target.value)}
                title="Read the stories in"
                aria-label="Read the stories in"
                className="absolute end-[7.5rem] top-2.5 max-w-[6.5rem] rounded-full border border-white/15 bg-panel px-2 py-1 text-[11px] text-ink-soft outline-none transition-colors hover:border-brand/50 focus:border-brand/60"
              >
                {(languages.length ? languages : [{ code: "en", name: "English" }]).map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
              )}
            </div>

            {calling && (
              <MiluCall
                sessionId={sessionRef.current}
                lang={lang}
                onEnd={() => {
                  localStorage.removeItem("cml_call_at");
                  setCalling(false);
                }}
                onRecap={keepRecap}
                onEyes={applyEyes}
                lessonLang={readLang}
                onLine={(who, text) => setMessages((was) => [...was,
                  { id: msgId(), role: who === "me" ? "user" : "milu", text }])}
              />
            )}

            {!calling && listenMenu && (
              <div className="shrink-0 border-b border-white/10 px-5 py-3" data-testid="milu-listen">
                <div className="flex items-center gap-2">
                  <p className="text-[11px] uppercase tracking-widest text-brand">Listen now</p>
                  <select
                    data-testid="milu-listen-voice"
                    value={listenMenu.voice}
                    onChange={(e) => readWith(e.target.value)}
                    title="Voice for this language"
                    aria-label="Voice for this language"
                    className="ms-auto max-w-[6.5rem] rounded-full border border-white/15 bg-panel px-2 py-1 text-[11px] text-ink-soft outline-none transition-colors hover:border-brand/50 focus:border-brand/60"
                  >
                    {READ_VOICES.map((v) => (
                      <option key={v.id} value={v.id}>{v.label.split(" · ")[0]}</option>
                    ))}
                  </select>
                  <button
                    data-testid="milu-listen-refresh"
                    onClick={() => loadListen(true)}
                    aria-label="Pull newer stories"
                    title="Newer stories"
                    className="rounded-full border border-white/15 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                  >
                    <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
                  </button>
                  <button
                    data-testid="milu-listen-toggle"
                    onClick={() => setListenOpen((v) => !v)}
                    aria-label={listenOpen ? "Hide the choices" : "Show the choices"}
                    className="rounded-full border border-white/15 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                  >
                    {listenOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                </div>
                {listenOpen && (
                  <div className="mt-2.5 max-h-[38vh] overflow-y-auto cml-scroll">
                    <MorningMenu menu={listenMenu} lang={readLang} hideGreeting autoPick={autoPick} />
                  </div>
                )}
              </div>
            )}

            <div ref={feedRef}
                 className={`min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4 md:max-h-[46vh] ${
                   calling ? "hidden" : ""}`}>
              {messages.map((m, i) => (
                <div
                  key={m.id}
                  className={`flex items-end gap-1.5 ${m.role === "user" ? "justify-end" : ""}`}
                >
                  <p
                    data-testid={`milu-msg-${i}`}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-brand text-white" : "bg-panel text-ink"
                    }`}
                  >
                    {m.text}
                  </p>
                  {m.role === "milu" && (
                    <button
                      data-testid={`milu-replay-${i}`}
                      onClick={() => say(m.text)}
                      aria-label="Read this out"
                      className="mb-1 shrink-0 text-ink-soft transition-colors hover:text-brand"
                    >
                      <Volume2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {busy && (
                <p className="flex items-center gap-2 text-xs text-ink-soft" data-testid="milu-typing">
                  <Loader2 className="h-3 w-3 animate-spin" /> MiLu is thinking
                </p>
              )}
            </div>

            {!calling && user && (
              <div className="flex shrink-0 items-center justify-end gap-3 px-5 pt-2">
                <button
                  data-testid="milu-minds-toggle"
                  onClick={() => {
                    setMinds((was) => (was ? null : []));
                    if (!minds) loadMinds();
                  }}
                  className="text-[11px] text-ink-soft transition-colors hover:text-brand"
                >
                  <Brain className="me-1 inline h-3 w-3" />
                  {minds ? "Hide memory" : "What I remember"}
                </button>
                <button
                  data-testid="milu-tongues-toggle"
                  onClick={() => {
                    setTongues((was) => !was);
                    setListenOpen(false);
                    if (!tongues) loadLevels();
                  }}
                  className="text-[11px] text-ink-soft transition-colors hover:text-brand"
                >
                  <GraduationCap className="me-1 inline h-3 w-3" />
                  {tongues ? "Hide my languages" : "My languages"}
                </button>
                <button
                  data-testid="milu-calls-toggle"
                  onClick={showCalls}
                  className="text-[11px] text-ink-soft transition-colors hover:text-brand"
                >
                  <History className="me-1 inline h-3 w-3" />
                  {pastCalls ? "Hide past calls" : "Past calls"}
                </button>
              </div>
            )}

            {minds && (
              <div data-testid="milu-minds"
                   className="max-h-[26vh] shrink-0 space-y-2 overflow-y-auto border-t border-white/10 px-5 py-3">
                <p className="text-[11px] uppercase tracking-widest text-brand">
                  What I remember about you
                </p>
                {minds.length === 0 && (
                  <p className="text-xs text-ink-soft">
                    Nothing yet. Say “remember that…” any time — in a chat or on a call — and I will
                    keep it.
                  </p>
                )}
                {minds.map((m) => (
                  <div key={m.memory_id} data-testid="milu-mind"
                       className="flex items-start gap-2 rounded-2xl border border-white/10 bg-panel px-3 py-2">
                    <p dir="auto" className="min-w-0 flex-1 text-xs text-ink">{m.fact}</p>
                    <span className="shrink-0 text-[9px] uppercase tracking-wide text-ink-soft">
                      {m.how === "noticed" ? "noticed" : "you asked"}
                    </span>
                    <button
                      data-testid={`milu-mind-forget-${m.memory_id}`}
                      onClick={() => forget(m.memory_id)}
                      aria-label="Forget this"
                      className="shrink-0 text-ink-soft transition-colors hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-white/10 pt-2">
                  <span className="text-[10px] uppercase tracking-widest text-ink-soft">
                    My eyes
                  </span>
                  {Object.entries(EYE_HEX).map(([name, hex]) => (
                    <button
                      key={name}
                      data-testid={`milu-eye-colour-${name}`}
                      onClick={() => wearEyes(name)}
                      aria-label={`Flash my eyes ${name}`}
                      title={name}
                      className={`h-5 w-5 rounded-full transition-transform hover:scale-110 ${
                        eyeWish === name ? "ring-2 ring-white ring-offset-2 ring-offset-panel" : ""}`}
                      style={{ backgroundColor: hex }}
                    />
                  ))}
                  {eyeWish && (
                    <button
                      data-testid="milu-eye-colour-clear"
                      onClick={() => wearEyes(eyeWish)}
                      className="text-[10px] text-ink-soft underline decoration-dotted hover:text-ink"
                    >
                      back to normal
                    </button>
                  )}
                  <button
                    data-testid="milu-surprise"
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/wingman/surprise");
                        applyEyes(data.eyes);
                        setReward(data.say);
                        toast.success(data.say);
                      } catch {
                        toast.error("Could not surprise you just now");
                      }
                    }}
                    className="ms-auto inline-flex items-center gap-1 rounded-full border border-brand/50 px-2 py-0.5 text-[10px] text-brand transition-colors hover:bg-brand/10"
                  >
                    <Sparkles className="h-2.5 w-2.5" /> Surprise me
                  </button>
                  <button
                    data-testid="milu-eye-hour-toggle"
                    onClick={() => {
                      const on = !byHour;
                      setByHour(on);
                      localStorage.setItem(HOUR_EYES_KEY, on ? "1" : "0");
                    }}
                    title="Bright by day with your theme, warm at dusk, deep at night"
                    className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors ${
                      byHour ? "border-brand/60 bg-brand/10 text-brand"
                             : "border-white/15 text-ink-soft"}`}
                  >
                    {byHour ? "by the hour" : "theme colour"}
                  </button>
                </div>

                {EYE_MOODS.map(([mood, label]) => (
                  <div key={mood} className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span className="w-[86px] shrink-0 text-[10px] uppercase tracking-widest text-ink-soft">
                      {label}
                    </span>
                    {Object.entries(EYE_HEX).map(([name, hex]) => (
                      <button
                        key={name}
                        data-testid={`milu-eye-${mood}-${name}`}
                        onClick={() => wearEyes(name, mood)}
                        aria-label={`${label} in ${name}`}
                        title={`${label} · ${name}`}
                        className={`h-4 w-4 rounded-full transition-transform hover:scale-110 ${
                          eyeMoods[mood] === name
                            ? "ring-2 ring-white ring-offset-2 ring-offset-panel" : ""}`}
                        style={{ backgroundColor: hex }}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}

            {tongues && (
              <div data-testid="milu-tongues"
                   className="max-h-[26vh] shrink-0 space-y-2 overflow-y-auto border-t border-white/10 px-5 py-3">
                <div className="flex items-center gap-2">
                  <p className="text-[11px] uppercase tracking-widest text-brand">Your languages</p>
                  <Link
                    data-testid="milu-progress-link"
                    to="/progress"
                    onClick={() => setOpen(false)}
                    className="text-[10px] text-ink-soft underline decoration-dotted transition-colors hover:text-brand"
                  >
                    See it all
                  </Link>
                  <label className="ms-auto flex items-center gap-1 text-[10px] text-ink-soft">
                    Calls a week
                    <select
                      data-testid="milu-goal-picker"
                      value={levels?.week?.goal || 3}
                      onChange={(e) => setGoal(Number(e.target.value))}
                      className="rounded-full border border-white/15 bg-panel px-1.5 py-0.5 text-[10px] text-ink outline-none hover:border-brand/50"
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </label>
                </div>
                {levels?.week && (
                  <p data-testid="milu-tongues-week"
                     className={`text-[11px] ${levels.week.hit ? "text-emerald-300" : "text-ink-soft"}`}>
                    {levels.week.hit
                      ? `${levels.week.done} practice calls this week — goal of ${levels.week.goal} hit, nicely done`
                      : `${levels.week.done} of ${levels.week.goal} practice calls this week`}
                  </p>
                )}
                {levels?.languages?.length === 0 && (
                  <p className="text-xs text-ink-soft">
                    Hold a call in a language you are learning and I will start marking it.
                  </p>
                )}
                {(levels?.languages || []).map((l) => (
                  <div key={l.lang} data-testid={`milu-tongue-${l.lang}`}
                       className="rounded-2xl border border-white/10 bg-panel px-3 py-2">
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-ink">{l.name}</p>
                      <span className="rounded-full border border-brand/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-brand">
                        {l.level}
                      </span>
                      <span className="ms-auto text-[10px] text-ink-soft">
                        {l.scores[0]}/100 · best {l.best}
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] text-ink-soft">{l.next_step}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <button
                        data-testid={`milu-tongue-drill-${l.lang}`}
                        onClick={() => {
                          setTongues(false);
                          setQuiz(null);
                          setDrill(l.lang);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-brand/50 px-2.5 py-0.5 text-[10px] text-brand transition-colors hover:bg-brand/10"
                      >
                        <Mic className="h-2.5 w-2.5" /> Practise drills
                      </button>
                      <button
                        data-testid={`milu-tongue-quiz-${l.lang}`}
                        onClick={() => {
                          setTongues(false);
                          setDrill(null);
                          setQuiz(l.lang);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-0.5 text-[10px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                      >
                        <GraduationCap className="h-2.5 w-2.5" /> Daily quiz
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  data-testid="milu-nudge-toggle"
                  onClick={async () => {
                    const on = !levels?.nudge;
                    setLevels((was) => (was ? { ...was, nudge: on } : was));
                    await api.put("/wingman/nudge", { on }).catch(() => {});
                    toast.success(on ? "I will send you a line each evening"
                                     : "No more evening lines");
                  }}
                  className={`inline-flex items-center gap-1.5 text-[10px] transition-colors ${
                    levels?.nudge ? "text-brand" : "text-ink-soft hover:text-ink"}`}
                >
                  {levels?.nudge ? <Ear className="h-3 w-3" /> : <EarOff className="h-3 w-3" />}
                  {levels?.nudge ? "One line to say each evening: on" : "Evening line: off"}
                </button>
              </div>
            )}

            {pastCalls && (
              <div data-testid="milu-calls" className="shrink-0 max-h-[26vh] space-y-2 overflow-y-auto border-t border-white/10 px-5 py-3">
                <p className="text-[11px] uppercase tracking-widest text-brand">Past calls</p>
                {pastCalls.length === 0 && (
                  <p className="text-xs text-ink-soft">No calls written up yet.</p>
                )}
                {pastCalls.map((c) => (
                  <div key={c.ended_at} className="rounded-2xl border border-white/10 bg-panel px-3 py-2">
                    <p className="text-[10px] text-ink-soft">{c.ended_at?.slice(0, 16).replace("T", " ")}</p>
                    <p className="mt-0.5 text-xs text-ink" dir="auto">{c.recap}</p>
                    {c.lesson?.score ? (
                      <button
                        data-testid="past-call-score"
                        onClick={() => showLesson(c.lesson)}
                        title="See this lesson report"
                        className="mt-1 inline-flex items-center gap-1 rounded-full bg-brand/15 px-2 py-0.5 text-[10px] text-brand transition-colors hover:bg-brand/25"
                      >
                        <GraduationCap className="h-2.5 w-2.5" />
                        {String(c.lesson.lang || "").toUpperCase()} {c.lesson.score}/100
                      </button>
                    ) : null}
                    {(c.todos || []).map((t) => (
                      <p key={t} className="mt-1 text-[11px] text-brand" dir="auto">• {t}</p>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {lesson && !calling && (
              <div
                data-testid="milu-lesson"
                ref={lessonRef}
                className="mx-5 mb-2 shrink-0 rounded-2xl border border-brand/30 bg-panel/80 px-3 py-2.5"
              >
                <div className="flex items-center gap-2">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-brand">
                    <GraduationCap className="h-3 w-3" />
                    {String(lesson.lang || "").toUpperCase()} lesson report
                  </p>
                  <span data-testid="milu-lesson-score"
                        className="ms-auto rounded-full bg-brand/15 px-2 py-0.5 text-xs font-semibold text-brand">
                    {lesson.score}/100
                  </span>
                  {lesson.hard && lesson.bonus ? (
                    <span data-testid="milu-lesson-hard"
                          className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-300">
                      only {String(lesson.lang || "").toUpperCase()} · +{lesson.bonus}
                    </span>
                  ) : null}
                  <button
                    data-testid="milu-lesson-close"
                    onClick={() => setLesson(null)}
                    aria-label="Close lesson report"
                    className="text-ink-soft transition-colors hover:text-ink"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>

                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
                  <span className="block h-full rounded-full bg-brand transition-[width] duration-700"
                        style={{ width: `${lesson.score}%` }} />
                </div>

                {langInfo(lesson.lang) && (
                  <>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span data-testid="milu-lesson-level"
                            className="rounded-full border border-brand/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-brand">
                        {langInfo(lesson.lang).level}
                      </span>
                      <span className="text-[10px] text-ink-soft">
                        {langInfo(lesson.lang).calls} lesson{langInfo(lesson.lang).calls === 1 ? "" : "s"} ·
                        best {langInfo(lesson.lang).best}
                      </span>
                    </div>
                    <p data-testid="milu-lesson-next" className="mt-1 text-[11px] text-ink-soft">
                      {langInfo(lesson.lang).next_step}
                    </p>
                  </>
                )}

                {(lesson.wins || []).length > 0 && (
                  <ul data-testid="milu-lesson-wins" className="mt-2 space-y-1">
                    {(lesson.wins || []).map((w) => (
                      <li key={w} dir="auto" className="flex gap-1.5 text-[11px] text-emerald-300">
                        <Check className="mt-0.5 h-3 w-3 shrink-0" /> {w}
                      </li>
                    ))}
                  </ul>
                )}

                {(lesson.slips || []).length > 0 && (
                  <ul data-testid="milu-lesson-slips" className="mt-2 space-y-1">
                    {(lesson.slips || []).map((s) => (
                      <li key={s} dir="auto" className="text-[11px] text-ink-soft">– {s}</li>
                    ))}
                  </ul>
                )}

                {lesson.practise && (
                  <p data-testid="milu-lesson-practise" dir="auto" className="mt-2 text-[11px] text-ink">
                    Worth practising: {lesson.practise}
                  </p>
                )}

                {trend.length > 1 && (
                  <div data-testid="milu-lesson-trend" className="mt-2.5 border-t border-white/10 pt-2">
                    <p className="text-[10px] uppercase tracking-widest text-ink-soft">Your last lessons</p>
                    <div className="mt-1.5 flex items-end gap-1.5">
                      {trend.map((t, i) => (
                        <span
                          key={`${t.at}-${i}`}
                          title={`${t.score}/100`}
                          className={`w-4 rounded-t ${i === trend.length - 1 ? "bg-brand" : "bg-brand/35"}`}
                          style={{ height: `${Math.max(6, (t.score / 100) * 34)}px` }}
                        />
                      ))}
                      <span className="ms-1 text-[10px] text-ink-soft">
                        {trend[trend.length - 1].score >= trend[0].score
                          ? `up ${trend[trend.length - 1].score - trend[0].score} points`
                          : `down ${trend[0].score - trend[trend.length - 1].score} points`}
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-2.5 flex flex-wrap items-center gap-2 border-t border-white/10 pt-2">
                  {levels?.week && (
                    <span data-testid="milu-week-goal"
                          className={`text-[10px] ${levels.week.hit ? "text-emerald-300" : "text-ink-soft"}`}>
                      {levels.week.hit
                        ? `${levels.week.done} practice calls this week — goal of ${levels.week.goal} hit`
                        : `${levels.week.done} of ${levels.week.goal} practice calls this week`}
                    </span>
                  )}
                  <button
                    data-testid="milu-quiz-open"
                    onClick={() => setQuiz(lesson.lang)}
                    className="ms-auto inline-flex items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                  >
                    <GraduationCap className="h-3 w-3" /> Daily quiz
                  </button>
                  <button
                    data-testid="milu-drill-open"
                    onClick={() => setDrill(lesson.lang)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-brand/50 px-2.5 py-1 text-[11px] text-brand transition-colors hover:bg-brand/10"
                  >
                    <Mic className="h-3 w-3" /> Practise drills
                  </button>
                </div>
              </div>
            )}

            {weekNights && !calling && (
              <div data-testid="milu-week-nights"
                   className="mx-5 mb-2 shrink-0 rounded-2xl border border-indigo-400/30 bg-indigo-500/5 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-indigo-300">
                    <Moon className="h-3 w-3" /> Your week of nights
                  </p>
                  <button data-testid="milu-week-nights-close" onClick={() => setWeekNights(null)}
                          aria-label="Close"
                          className="ms-auto text-ink-soft transition-colors hover:text-ink">
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <p data-testid="milu-week-nights-say" dir="auto" className="mt-1.5 text-sm text-ink">
                  {weekNights.say}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    data-testid="milu-week-nights-read"
                    onClick={() => say(weekNights.say)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-indigo-400/50 px-2.5 py-1 text-[11px] text-indigo-300 transition-colors hover:bg-indigo-500/10"
                  >
                    <Volume2 className="h-3 w-3" /> Read it to me
                  </button>
                  <Link
                    to="/progress"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center text-[11px] text-ink-soft hover:text-brand"
                  >
                    See the month →
                  </Link>
                </div>
              </div>
            )}

            {night && !calling && (
              <div data-testid="milu-goodnight"
                   className="mx-5 mb-2 shrink-0 rounded-2xl border border-indigo-400/30 bg-indigo-500/5 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-indigo-300">
                    <Moon className="h-3 w-3" /> Winding down
                  </p>
                  <button data-testid="milu-goodnight-close" onClick={() => setNight(null)}
                          aria-label="Close"
                          className="ms-auto text-ink-soft transition-colors hover:text-ink">
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <p data-testid="milu-goodnight-say" dir="auto" className="mt-1.5 text-sm text-ink">
                  {night.say}
                </p>
                {night.tomorrow?.length > 0 && (
                  <ul data-testid="milu-tomorrow" className="mt-2 space-y-1">
                    {night.tomorrow.map((t) => (
                      <li key={`${t.title}-${t.start}`} dir="auto"
                          className="flex gap-2 text-[11px] text-ink-soft">
                        <span className="text-indigo-300">{t.start || "—"}</span> {t.title}
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  data-testid="milu-goodnight-ok"
                  onClick={() => {
                    setNight(null);
                    if (voiceOn) say(night.say);
                  }}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-indigo-400/50 px-2.5 py-1 text-[11px] text-indigo-300 transition-colors hover:bg-indigo-500/10"
                >
                  <Moon className="h-3 w-3" /> Goodnight, MiLu
                </button>
                <div className="mt-2 border-t border-white/10 pt-2">
                  <p data-testid="milu-journal-ask" dir="auto" className="text-[11px] text-indigo-200">
                    {night.prompt}
                  </p>
                  <div className="mt-1.5 flex items-center gap-1.5">
                  <input
                    data-testid="milu-journal-input"
                    value={jotted}
                    onChange={(e) => setJotted(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && keepNight()}
                    placeholder="Today in one line…"
                    className="min-w-0 flex-1 rounded-full border border-white/15 bg-panel px-3 py-1.5 text-[11px] text-ink outline-none placeholder:text-ink-soft/70 focus:border-indigo-400/60"
                  />                  <button
                    data-testid="milu-journal-save"
                    onClick={keepNight}
                    disabled={!jotted.trim()}
                    className="shrink-0 rounded-full border border-indigo-400/50 px-2.5 py-1.5 text-[11px] text-indigo-300 transition-colors hover:bg-indigo-500/10 disabled:opacity-50"
                  >
                    Keep it
                  </button>
                  </div>
                </div>
              </div>
            )}

            {learned && !calling && (
              <div data-testid="milu-learned"
                   className="mx-5 mb-2 shrink-0 rounded-2xl border border-brand/30 bg-panel/80 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-brand">
                    <Brain className="h-3 w-3" /> What I learned this week
                  </p>
                  <button data-testid="milu-learned-close" onClick={() => setLearned(null)}
                          aria-label="Close"
                          className="ms-auto text-ink-soft transition-colors hover:text-ink">
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <ul className="mt-1.5 space-y-1">
                  {learned.learned.slice(0, 6).map((m) => (
                    <li key={m.memory_id} dir="auto" className="text-[11px] text-ink">– {m.fact}</li>
                  ))}
                </ul>
                <button
                  data-testid="milu-learned-all"
                  onClick={() => {
                    setLearned(null);
                    setMinds([]);
                    loadMinds();
                  }}
                  className="mt-2 text-[11px] text-brand hover:underline"
                >
                  See everything I remember ({learned.kept}) →
                </button>
              </div>
            )}

            {tonight && !calling && (
              <div data-testid="milu-tonight"
                   className="mx-5 mb-2 shrink-0 rounded-2xl border border-amber-400/30 bg-amber-500/5 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <p className="text-[11px] uppercase tracking-widest text-amber-300">
                    Tonight's line{tonight.name ? ` · ${tonight.name}` : ""}
                  </p>
                  <button data-testid="milu-tonight-close" onClick={closeTonight}
                          aria-label="Not tonight"
                          className="ms-auto text-ink-soft transition-colors hover:text-ink">
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <p data-testid="milu-tonight-line" dir="auto" className="mt-1.5 text-sm text-ink">
                  {tonight.line}
                </p>
                {tonight.meaning && (
                  <p dir="auto" className="text-[11px] text-ink-soft">{tonight.meaning}</p>
                )}
                <button
                  data-testid="milu-tonight-practise"
                  onClick={() => {
                    closeTonight();
                    setQuiz(null);
                    setDrill(tonight.lang || "");
                  }}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-400/50 px-2.5 py-1 text-[11px] text-amber-300 transition-colors hover:bg-amber-500/10"
                >
                  <Mic className="h-3 w-3" /> Say it with me
                </button>
              </div>
            )}

            {quiz && !calling && (
              <MiluQuiz lang={quiz === "all" ? "" : quiz} onClose={() => setQuiz(null)} />
            )}

            {drill && !calling && (
              <MiluDrill lang={drill} onClose={() => setDrill(null)} />
            )}

            {todos.length > 0 && !calling && (
              <button
                data-testid="milu-todos-to-calendar"
                onClick={toCalendar}
                className="mx-5 mb-2 inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-brand/50 px-3 py-1.5 text-[11px] text-brand transition-colors hover:bg-brand/10"
              >
                <CalendarPlus className="h-3 w-3" />
                Put {todos.length} to-do{todos.length > 1 ? "s" : ""} on my calendar
              </button>
            )}

            {!calling && (chips.length > 0 || cta) && (
              <div className="flex shrink-0 flex-wrap gap-2 px-5 pb-3">
                {chips.map((c, i) => (
                  <button
                    key={c}
                    data-testid={`milu-chip-${i}`}
                    onClick={() => send(c)}
                    className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-brand hover:text-ink"
                  >
                    {c}
                  </button>
                ))}
                {cta && (
                  <Link
                    to={cta.href}
                    data-testid="milu-cta"
                    onClick={() => {
                      localStorage.setItem(SESSION_KEY, sessionRef.current);
                      track("cta_click", cta.href);
                      setCheering(true);
                      setOpen(false);
                    }}
                  >
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3.5 py-1.5 text-xs font-semibold text-white transition-transform hover:scale-[1.03]">
                      <Sparkles className="h-3 w-3" /> {cta.label}
                    </span>
                  </Link>
                )}
              </div>
            )}

            <div className="flex shrink-0 items-center gap-2 border-t border-white/10 px-4 py-3">
              <button
                data-testid="milu-mic"
                onClick={listen}
                disabled={busy && !listening}
                aria-label={listening ? "Stop recording" : "Talk to MiLu"}
                className={`h-[42px] w-[42px] shrink-0 rounded-full border transition-colors ${
                  listening
                    ? "recording border-[#ff5a72]/60 bg-[#ff5a72]/15 text-[#ff5a72]"
                    : "border-white/15 text-ink-soft hover:border-brand hover:text-brand"
                } flex items-center justify-center`}
              >
                {listening ? <Square className="h-3.5 w-3.5" /> : <Mic className="h-4 w-4" />}
              </button>
              <Input
                data-testid="milu-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={listening ? "Listening…" : "Talk or type…"}
                className="min-h-[42px] rounded-full border-white/10 bg-panel text-sm"
              />
              <Button
                data-testid="milu-send"
                onClick={() => send()}
                disabled={busy}
                className="h-[42px] w-[42px] shrink-0 rounded-full bg-brand p-0 hover:bg-brand-hover active:scale-90"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <button data-testid="milu-open" onClick={start} className="w-full text-start">
            <div
              className={`rise rounded-3xl border border-brand/30 bg-surface/95 px-5 py-4 pe-10 shadow-[0_18px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl transition-transform duration-300 hover:-translate-y-0.5 ${
                (isQuiet || pitchless) && !celebrate && !reward
                  ? "hidden"
                  : showCard ? "block" : "hidden sm:block"
              }`}
            >
              <p
                key={pitchIdx}
                data-testid="milu-pitch"
                className="rise line-clamp-3 text-sm leading-relaxed text-ink"
              >
                {pitch.say}
              </p>
              <span className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-brand">
                <Sparkles className="h-3 w-3" /> Chat with MiLu
              </span>
            </div>
          </button>
        )}

        <div className="relative">
          {mood === "cheer" && (
            <span aria-hidden className="pointer-events-none absolute inset-0 z-10" data-testid="milu-sparkles">
              {/* fixed decorative burst — the index is a stable key here */}
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span
                  key={`spark-${i}`}
                  className="milu-spark absolute bottom-[55%] end-[42%] h-1.5 w-1.5 rounded-full bg-brand"
                  style={{ animationDelay: `${i * 110}ms`, ["--dx"]: `${(i - 2.5) * 26}px` }}
                />
              ))}
            </span>
          )}
          {!open && !calling && (
            <button
              data-testid="milu-call-anywhere"
              onClick={() => setCalling(true)}
              title="Call MiLu"
              aria-label="Call MiLu"
              className="pointer-events-auto ms-auto mb-1 flex items-center gap-1.5 rounded-full border border-brand/50 bg-surface/95 px-3 py-1.5 text-[11px] text-brand shadow-[0_10px_24px_rgba(0,0,0,0.45)] backdrop-blur transition-transform hover:-translate-y-0.5"
            >
              <Phone className="h-3 w-3" /> Call
            </button>
          )}

          <span
            ref={mascotRef}
            className={`${MOOD_ANIM[mood] || MOOD_ANIM.idle} pointer-events-auto relative ms-auto block w-fit`}
          >
            <span className="milu-lean relative block">
              <img
                src={SPRITES[mood] || SPRITES.idle}
                alt="MiLu"
                data-testid="milu-mascot"
                data-mood={mood}
                onClick={start}
                className={`block w-auto cursor-pointer select-none drop-shadow-[0_18px_28px_rgba(0,0,0,0.55)] ${
                  open || showCard ? "-mt-3 h-24 sm:-mt-4 sm:h-40" : "h-20 sm:h-28"
                }`}
              />
              <MiluEyes sprite={SPRITES[mood] || SPRITES.idle} tone={eyeTone}
                        hue={eyeHue(eyeTone)} />
            </span>
          </span>
        </div>
      </div>
    </div>
  );
  };

  return (
    <>
      {resumeBar}
      {renderBody()}
    </>
  );
};
